/**
 * Shared constants and utilities for call log logic.
 * Used by both CallLog.jsx and EmployeeOvertimeItem.jsx to ensure
 * shift-card unlock behavior stays in sync for both locally-logged
 * and API-loaded entries.
 */

export const CALL_TYPES = {
  OUTBOUND: 'Outbound',
  INBOUND: 'Inbound',
};

export const ANSWER_TYPES = {
  ANSWERED: 'answered',
  NO_ANSWER: 'no answer',
  VOICEMAIL: 'received voicemail',
};

export const normalizeAction = (action = '') =>
  String(action || '').toUpperCase().trim();

export const normalizeActionToken = (action = '') =>
  normalizeAction(action).replace(/[\s_-]/g, '');

export const isNoContactAction = (actionValue = '') =>
  normalizeActionToken(actionValue) === 'NOCONTACT';

export const parseCallLogTimestampMs = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return NaN;

  // API format: "HH:MM MM/DD/YYYY"
  const apiMatch = raw.match(/^(\d{1,2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})$/);
  if (apiMatch) {
    const [, h, m, mo, d, y] = apiMatch;
    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(m)).getTime();
  }

  // Station format: "YYYY-MM-DD HH:mm:ss.SSS"
  const stationMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/);
  if (stationMatch) {
    const [, y, mo, d, h, m, s = '0', ms = '0'] = stationMatch;
    return new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(m),
      Number(s),
      Number(String(ms).padEnd(3, '0'))
    ).getTime();
  }

  return new Date(raw).getTime();
};

export const parseCallLogEntryTimestampMs = (entry = {}) =>
  parseCallLogTimestampMs(entry?.timestamp || entry?.lastUpdated || '');

export const normalizeShiftType = (value = '') =>
  String(value || '').toUpperCase().trim();

export const normalizeShiftToken = (value = '') =>
  normalizeShiftType(value).replace(/^SHIFT\s*/i, '').trim();

export const entryMatchesCurrentShift = (entry = {}, currentShiftType = '') => {
  const currentShift = normalizeShiftType(currentShiftType);
  if (!currentShift) return true;

  const entryShift = normalizeShiftType(entry?.shiftType || entry?.shiftName || '');
  const entryLabelShift = normalizeShiftType(entry?.shiftLabel || '');
  if (entryShift === currentShift || entryLabelShift === currentShift) return true;

  const currentShiftShort = normalizeShiftToken(currentShift);
  const entryShiftShort = normalizeShiftToken(entryShift);
  const entryLabelShort = normalizeShiftToken(entryLabelShift);
  return entryShiftShort === currentShiftShort || entryLabelShort === currentShiftShort;
};

export const formatShiftTimeToHourMinute = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const match =
    raw.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/) ||
    raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hourNum = parseInt(match[1], 10);
  const minuteNum = parseInt(match[2], 10);
  if (
    Number.isNaN(hourNum) ||
    Number.isNaN(minuteNum) ||
    hourNum < 0 ||
    hourNum > 23 ||
    minuteNum < 0 ||
    minuteNum > 59
  ) {
    return null;
  }

  return `${String(hourNum).padStart(2, '0')}:${String(minuteNum).padStart(2, '0')}`;
};

export const formatDisplayHourMinute = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = formatShiftTimeToHourMinute(raw);
  if (normalized) return normalized;

  // Preserve legacy behavior for plain "HH:mm[:ss]" or "YYYY-MM-DD HH:mm:ss" values.
  const timePart = raw.includes(' ') ? raw.split(' ')[1] : raw;
  const [hours = '', minutes = ''] = timePart.split(':');
  if (!hours || !minutes) return '';
  return `${hours}:${minutes}`;
};

export const normalizeSlotTimeToken = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalizedTime = formatShiftTimeToHourMinute(raw);
  return normalizedTime || normalizeShiftType(raw);
};

export const hasSlotDetails = (entry = {}) =>
  Boolean(
    String(entry?.startTime || '').trim() ||
    String(entry?.endTime || '').trim() ||
    String(entry?.workLocation || '').trim()
  );

/**
 * Returns true when the call log entry qualifies to unlock shift-card editing.
 * Business rule: answered outbound calls and inbound contact attempts can unlock card actions.
 *
 * Handles two entry shapes:
 *  - API-loaded:  { type, answered }
 *  - Locally-constructed: { callType, isAnswered (boolean) }
 *
 * @param {object} entry - Call log entry object.
 * @returns {boolean}
 */
export const isShiftCardEditUnlockingEntry = (entry) => {
  // Require entry to have EITHER type or callType (reject system entries with neither)
  if (!entry?.type && !entry?.callType) return false;
  
  // Normalise call type: prefer `type`, fall back to `callType`
  const normalizedType = (entry?.type || entry?.callType || '').toLowerCase();
  const isInboundType = normalizedType === CALL_TYPES.INBOUND.toLowerCase();
  const isOutboundType = normalizedType === CALL_TYPES.OUTBOUND.toLowerCase();

  // Require valid call type (no empty strings)
  if (!isInboundType && !isOutboundType) return false;

  // Normalise answered field: prefer `answered` string, fall back to `isAnswered` boolean
  const normalizedAnswered = (
    entry?.answered ||
    (entry?.isAnswered === true ? ANSWER_TYPES.ANSWERED : '')
  ).toLowerCase();

  const hasVoicemailApiShape =
    isInboundType &&
    entry?.answered == null &&
    entry?.isAnswered === false &&
    typeof entry?.isCallReturned === 'boolean';

  const isAnsweredOutbound =
    isOutboundType &&
    normalizedAnswered === ANSWER_TYPES.ANSWERED;

  return (
    isAnsweredOutbound ||
    (isInboundType && normalizedAnswered === ANSWER_TYPES.ANSWERED) ||
    (isInboundType && normalizedAnswered === ANSWER_TYPES.VOICEMAIL) ||
    hasVoicemailApiShape
  );
};
